import { Suspense, useEffect, useState, useMemo } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Box, Modal } from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
import Fade from "@mui/material/Fade";
import { Auth } from "aws-amplify";
import DefaultLoader from "../DefaultLoader";
import { newuiRoutes } from "routes";
import "../../assets/css/style.css";
import { ThemeContextProvider } from "../ContextProvider";
import { fetchJSONFromS3 } from "services/Helper";
import { getDocList } from "classic/components/Utils/ExtractUtils";
import Layout from "../Layout";
import SafeWrapper from "../SaveChanges";
import { createUiLogger } from "../../../logging/uiLogger";
const console = createUiLogger("meaplatform/newui/components/NewUiRoutes/index.jsx");



const NotFoundPage = () => (
  <div className="newui-bad-url">
    <span>404 - Page Not Found.</span>
  </div>
);

const fetchMenuItems = async () => {
  const filePath = `system/config/uiConfiguration.json`;
  try {
    const fileData = await fetchJSONFromS3(filePath);
    return fileData?.menuItems || {};
  } catch (error) {
    console.error("Failed to fetch UI configuration:", error);
    return {};
  }
};

const isMenuItemAllowedForRoles = (item, userGroups) => {
  if (!Array.isArray(item?.groups)) {
    return true;
  }

  const groups = Array.isArray(userGroups) ? userGroups : [];
  return groups.some((group) => item.groups.includes(String(group || "").trim()));
};

const NewUiRoutes = ({ toggleSwitchUI }) => {
  const [menuItems, setMenuItems] = useState({});
  const [userGroups, setUserGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessingDeepLink, setIsProcessingDeepLink] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showModalMsg, setShowModalMsg] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      try {
        const items = await fetchMenuItems();
        setMenuItems(items);
        const user = await Auth.currentAuthenticatedUser({
          bypassCache: true,
        });
        const groups = user.signInUserSession?.idToken?.payload?.["cognito:groups"];
        setUserGroups(Array.isArray(groups) ? groups : []);
      } catch (error) {
        console.error("Failed to load UI route configuration:", error);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    const checkDirectQATool = async () => {
      const pattern = /^\/user\/submissions\/qatool\/([^/]+)(?:\/([^/]+)\/([^/]+))?\/?$/;
      let pathname = location.pathname;
      if (localStorage.getItem("navigator") !== null) {
        pathname = localStorage.getItem("navigator");
        localStorage.removeItem("navigator");
      }

      const match = pathname.match(pattern);

      if (match){
        setIsProcessingDeepLink(true);
        const submissionIdentifier = decodeURIComponent(match[1]);
        const templateName = match[2];
        const documentName = match[3];
        console.info(`Loading submissionIdentifier: ${submissionIdentifier}`);
        try {
          const user = await Auth.currentAuthenticatedUser({
            bypassCache: true,
          });
          const groups = user.signInUserSession.idToken.payload["cognito:groups"];
          let userRole = "MEA_USERS";
          if (groups !== undefined) {
            if (groups.includes("MEA_PROCESSORS")) {
              userRole = "MEA_PROCESSORS";
            } else if (groups.includes("MEA_ADMIN")) {
              userRole = "MEA_ADMIN";
            } else if (groups.includes("MEA_UNDERWRITER")) {
              userRole = "MEA_UNDERWRITER";
            } else if (groups.includes("MEA_CORPORATE")) {
              userRole = "MEA_CORPORATE";
            }
          }

          let docs = await getDocList({ submissionIdentifier });
          const decodedTemplate = templateName
            ? decodeURIComponent(templateName)
            : undefined;
          const decodedDoc = documentName
            ? decodeURIComponent(documentName)
            : undefined;

          if (!docs || docs.length === 0) {
            setShowModalMsg(`Submission Identifier ${submissionIdentifier} with template ${decodedTemplate} is not found.\n Please verify the submission identifier associated with this template exists in submissions list.`);
            setShowModal(true);
            setIsProcessingDeepLink(false);
            navigate("/user/submission-summary", { replace: true });
            return;
          }

          let row = null;
          if (templateName && documentName) {
            row = docs.find(
              (item) =>
                item.templateName === decodedTemplate &&
                item.documentName === decodedDoc
            );

            if (!row) {
              console.warn(`Document ${decodedDoc} with template ${decodedTemplate} is not found.`);
              navigate("/user/submission-summary", {
                state: { submissionIdentifier },
                replace: true
              });             
              setShowModalMsg(`Document ${decodedDoc} with template ${decodedTemplate} is not found. Please verify the document associated with this template exists in submissions list.`);
              setShowModal(true);
              setIsProcessingDeepLink(false);
              return;
            }
          } else {
            let msg =
              docs.length === 1
                ? docs
                : docs.filter((item) => item.isMerged === 1);

            if (msg?.length === 1) {
              row = msg[0];
            } else {
              console.warn(`invalid action on submissionIdentifier: ${submissionIdentifier}`);
              navigate("/user/submission-summary", { replace: true });
              return;
            }
          }

          if (
            (row?.jsonMarkCompletePath || row?.jsonExtractpath) &&
            (userRole === "MEA_CORPORATE" ||
              userRole === "MEA_UNDERWRITER" ||
              userRole === "MEA_ADMIN") &&
            (row.processingStatus === "Complete" ||
              row.processingStatus === "QA In Progress" ||
              row.processingStatus === "QA Uploaded" ||
              row.processingStatus === "QA Complete")
          ) {
            navigate("/user/qa-tool", {
              state: { extractToolData: row, isArchived: false },
              replace: true,
            });
          } else {
            console.debug(`no merge record for submissionIdentifier: ${submissionIdentifier}`);
            navigate("/user/submission-summary", { replace: true });
          }
        } catch (error) {
          console.error("Error processing direct QA tool link:", error);
          navigate("/user/submission-summary", { replace: true });
        } finally {
          setIsProcessingDeepLink(false);
        }
      }
    };
    
    checkDirectQATool();
  }, [location.pathname, navigate]);

  const enabledRoutes = useMemo(() => {
    const allItems = [
      ...(menuItems.Ingestion?.items || []),
      ...(menuItems.Products?.items || []),
      ...(menuItems.Others?.items || []),
      ...(menuItems.Premium ? [menuItems.Premium] : []),
    ];

    const routeSet = new Set();
    for (const item of allItems) {
      if (
        item.enabled &&
        item.route &&
        isMenuItemAllowedForRoles(item, userGroups)
      ) {
        routeSet.add(item.route);
      }
    }
    return routeSet;
  }, [menuItems, userGroups]);

  return (
    <ThemeContextProvider>
      <Routes>
        <Route path="/" element={<Layout toggleSwitchUI={toggleSwitchUI} />}>
          {newuiRoutes.map((route, index) => {
            const Component = route.component;
            const fullPath = route.layout + route.path;

            const isRouteEnabled =
              route.alwaysEnabled || enabledRoutes.has(fullPath);

            return (
              <Route
                key={`newuiRoutes-${index}`}
                path={fullPath}
                element={
                  <SafeWrapper>
                    <Suspense fallback={<></>}>
                      {isRouteEnabled ? (
                        <Component toggleSwitchUI={toggleSwitchUI} />
                      ) : (
                        <NotFoundPage />
                      )}
                    </Suspense>
                  </SafeWrapper>
                }
              />
            );
          })}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
      <Modal
        aria-labelledby="loading-modal"
        aria-describedby="loading-modal-description"
        className="modal-centre submissions-headermodal"
        open={isLoading || isProcessingDeepLink}
        closeAfterTransition
        disableEscapeKeyDown
      >
        <Box className="modal-layout d-flex-content-center">
          <Box className="modal-bglayer businessmodal-bglayer">
            <Box className="text-center my-3">
              <DefaultLoader loading={isLoading || isProcessingDeepLink} />
            </Box>
            <Box className="modal-footer-new d-flex-content-center">
              <Box className="modal-footerTitle">
                Powered by <span className="fw-600">mea platform</span>
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        className="submissions-headermodal"
        open={showModal}
        onClose={() => setShowModal(false)}
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 500 } }}
      >
        <Fade in={showModal}>
          <Box className="modal-layout d-flex-content-center">
            <Box className="modal-bglayer businessmodal-bglayer">
              <Box className="modal-message p-2">
                <p>{showModalMsg}</p>
              </Box>
              <Box className="d-flex-content-center businessmodel-button-container">
                <button
                  onClick={() => {
                    setShowModal(false);
                  }}
                  className="modal-button business-button-modal"
                >
                  <span className="modal-extractiontext">Close</span>
                </button>
              </Box>
              <Box className="modal-footer-new d-flex-content-center">
                <Box className="modal-footerTitle">
                  Powered by <span className="fw-600">mea platform</span>
                </Box>
              </Box>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </ThemeContextProvider>
  );
};

export default NewUiRoutes;
